import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {useNavigationState} from '../navigation-provider'
import {flyoutRecipe} from './index.recipe'
import FlyoutNavigation from './partials/flyout-navigation'
import {rested} from './partials/flyout-motion'
import {isReachable} from './partials/flyout-focus'
import {useNavigationContent} from '../../../../hooks/useScapiCustomApi'
import useIsomorphicLayoutEffect from '../../../../hooks/use-isomorphic-layout-effect'

/**
 * Test handle for the reveal grid. Exported so a rename cannot leave the tests
 * silently querying an id nothing carries.
 */
export const FLYOUT_REVEAL_TESTID = 'flyout-reveal'

/**
 * Test handle for the scrollport. Exported so a rename cannot leave the tests
 * silently querying an id nothing carries.
 */
export const FLYOUT_SCROLLER_TESTID = 'flyout-scroller'

/**
 * Backdrop for the navigation flyout.
 *
 * Rendered as a sibling of the header card (outside its `overflow: hidden`) so
 * it can dim the whole page. A `<button>` rather than a `<div>` so the "click to
 * dismiss" affordance is keyboard- and screen-reader-reachable.
 *
 * It stays mounted while closed rather than unmounting: an element removed on
 * close takes its own fade-out with it, and the backdrop's fade *is* the mobile
 * open animation. `visibility: hidden` keeps the closed backdrop out of the a11y
 * tree and off the hit-test, so staying mounted costs nothing (see the recipe,
 * which also keeps its `backdrop-filter` from standing while closed).
 *
 * @component
 * @returns {JSX.Element}
 */
export const NavBackdrop = () => {
    const {open, actions, backdropRef} = useNavigationState()
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: flyoutRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Box
            as="button"
            type="button"
            ref={backdropRef}
            data-state={open ? 'open' : 'closed'}
            // Unreachable while closed: `visibility: hidden` takes it out of the
            // tab order, and this keeps it off the a11y tree with it.
            aria-hidden={open ? undefined : true}
            tabIndex={open ? undefined : -1}
            css={styles.backdrop}
            aria-label={formatMessage({
                id: 'header.flyout.backdrop',
                defaultMessage: 'Close navigation'
            })}
            onClick={actions.close}
        />
    )
}

if (process.env.NODE_ENV !== 'production') {
    NavBackdrop.displayName = 'NavBackdrop'
}

/**
 * In-header expanding navigation flyout.
 *
 * A single responsive component: a full-screen push-pane on mobile and a
 * megamenu on desktop, differentiated only through responsive recipe values so
 * a resize preserves open state without remounting. The reveal is driven purely
 * in CSS (`gridTemplateRows: 0fr → 1fr` keyed on `data-state`); nothing here
 * measures panel height in JavaScript.
 *
 * Accessibility: the panel is a labelled modal `dialog` (`aria-modal`) on both
 * viewports — the header's focus lock (see `header/index.jsx`) contains Tab
 * within the header and flyout while open. Opening moves focus to the first
 * control of the pane on screen (see `FlyoutNavigation`), and Escape closes; on
 * close focus returns to the trigger.
 *
 * The body renders the CMS-authored navigation tree via `FlyoutNavigation`
 * (see that file). The v3-native visual redesign of the flyout body is a
 * separate design-refresh ticket.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - Region id, tied to the triggers' `aria-controls`.
 * @returns {JSX.Element}
 */
const Flyout = ({id}) => {
    const {open, intent, backdropRef} = useNavigationState()
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: flyoutRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    // Warmed here, not in the tree, which is unmounted while shut. `intent` is
    // pointer or focus on the trigger, and lands the content before the click:
    // the desktop panel is content-sized, so a fetch gated on `open` alone would
    // expand it around an empty box. The tree reads the same query from this
    // cache on mount.
    useNavigationContent({}, {enabled: open || intent})

    // The content's exit animation is keyed on the closed state, which also
    // matches on the very first paint — so without this every row would play its
    // exit on page load. Adjusted during render rather than in an effect so it
    // lands in the same commit as `open`, and the first open still animates in;
    // an effect would set it a frame late and cost the opening frame.
    const [hasOpenedOnce, setHasOpenedOnce] = useState(false)
    if (open && !hasOpenedOnce) setHasOpenedOnce(true)

    const restoreFocusRef = useRef(null)
    const wasOpenRef = useRef(open)
    // The scrollport is the body rather than the panel (see the recipe), so the
    // tree cannot reach it by walking up to the dialog — it is handed the node.
    const scrollerRef = useRef(null)
    // What the close is measured on: it owns the canvas collapse, the last
    // motion to finish.
    const revealRef = useRef(null)

    // The tree outlives `open` by the length of the close: removed the moment
    // the flag flips, it takes its own exit animation with it. Raised during
    // render so the opening commit already carries the content.
    const [mounted, setMounted] = useState(open)
    if (open && !mounted) setMounted(true)

    useEffect(() => {
        if (open || !mounted) return undefined
        let cancelled = false
        rested(revealRef.current).then(() => {
            if (!cancelled) setMounted(false)
        })
        // Reopening mid-close keeps the tree: this runs on the commit that
        // raises `open`, so the abandoned close cannot remove it.
        return () => {
            cancelled = true
        }
    }, [open, mounted])

    // A layout effect, so the trigger is read before the tree's own effect moves
    // focus into the panel.
    useIsomorphicLayoutEffect(() => {
        if (open && !wasOpenRef.current) {
            // Remember whichever trigger opened the flyout.
            restoreFocusRef.current = document.activeElement
        } else if (!open && wasOpenRef.current) {
            // Pull focus back to that trigger — the panel holding it is now
            // hidden and can no longer hold focus.
            //
            // Skipped when focus sits on a control that keeps it: a close caused
            // by something else taking focus, such as the account panel's
            // trigger, must leave it there (STOK-17609).
            const active = document.activeElement
            // the backdrop transitions its `visibility`, so it still reads
            // focusable while it fades - ask the tree, not the style
            const ours = revealRef.current?.contains(active) || backdropRef.current === active
            // `body` is where a lost focus lands, and asking the lock about it
            // would walk the whole document
            const lost = !active || active === document.body || !isReachable(active)

            if (ours || lost) {
                restoreFocusRef.current?.focus?.()
            }
            restoreFocusRef.current = null
        }
        wasOpenRef.current = open
    }, [open, backdropRef])

    const state = open ? 'open' : 'closed'

    return (
        <Box
            ref={revealRef}
            css={styles.reveal}
            data-state={state}
            data-motion={hasOpenedOnce ? 'ready' : undefined}
            // Exception (docs/agents/review-testing.md): the reveal grid is a
            // layout container with no ARIA role or accessible name a test could
            // target, and `data-motion` is only observable on it — data-testid is
            // the only handle on it.
            data-testid={FLYOUT_REVEAL_TESTID}
        >
            <Box
                id={id}
                role="dialog"
                aria-modal={open ? true : undefined}
                aria-hidden={open ? undefined : true}
                aria-label={formatMessage({
                    id: 'header.flyout.label',
                    defaultMessage: 'Main navigation'
                })}
                // The tree is still mounted for the length of the close, so
                // Tab would otherwise reach links in a clipped panel and scroll
                // it to chase focus. `inert` flips with the logical state
                // rather than the motion, so nobody waits on an animation.
                //
                // Empty string rather than `true`: React 18 renders booleans for
                // unknown attributes as the string "false", which is still a
                // present attribute and would make the panel permanently inert.
                inert={open ? undefined : ''}
                css={styles.panel}
            >
                <Box
                    ref={scrollerRef}
                    // The body is the flyout's own scroll container, but Lenis
                    // intercepts wheel/touch on the page wrapper and only
                    // exempts Chakra overlay bodies (see `prevent` in
                    // `contexts/lenis.jsx`); this is not one, so without this
                    // hint the wheel is swallowed and the flyout scrolls only
                    // via the scrollbar thumb. The attribute tells Lenis to
                    // leave native scrolling to it.
                    data-lenis-prevent
                    // Exception (docs/agents/review-testing.md): the scrollport
                    // is a layout container with no ARIA role or accessible
                    // name a test could target — the dialog role sits on the
                    // panel above it — so data-testid is the only handle on it.
                    data-testid={FLYOUT_SCROLLER_TESTID}
                    css={styles.body}
                >
                    {mounted && <FlyoutNavigation scrollerRef={scrollerRef} />}
                </Box>
            </Box>
        </Box>
    )
}

Flyout.propTypes = {
    id: PropTypes.string.isRequired
}

if (process.env.NODE_ENV !== 'production') {
    Flyout.displayName = 'Flyout'
}

export default Flyout
